import { createFileRoute } from '@tanstack/react-router';
import { enable2FAflowSchema } from '../../-model/flow';
import {
  ErrorCodes,
  logger,
  ModuleName,
  Screen,
  type SkinPageConfig,
  widgets,
  type NOSIDError,
  templates,
} from '@nosinovacao/nosid-mfe-common';
import { ErrorSidePanel } from '@/components/errors';
import { useSendCode } from '@/hooks/api/contact-validation.hooks';
import { useGetPersonalInfo } from '@/hooks/api';
import { useEffect } from 'react';
import { type TwoFactorCodeProps } from './-model/props';
import { useForm } from '@deadcow-enterprises/react-forms';
import z from 'zod';
import { useEnable2FA } from './-hooks/enable-2fa.hook';
import { TwoFactorCodeLight } from './-skins';
import { type AxiosError } from 'axios';
import { loadHistoryState } from '@/lib';

export const Route = createFileRoute('/(security)/security/_sheet/two-factor-code/')({
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
  staticData: {
    module: ModuleName.Security,
    screen: Screen.CodeInput,
  },
  loader: loadHistoryState(enable2FAflowSchema, {
    errorCode: ErrorCodes.AccessDenied,
  }),
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { target } = Route.useLoaderData();
  const { captcha, sendCode, isSending } = useSendCode();
  const { mutateAsync: enable2Fa } = useEnable2FA();
  const { data, isLoading } = useGetPersonalInfo();
  const phoneNumber = data?.UsernameAliases.find((al) => al.Type === 'Mobile')?.Username;
  useEffect(() => {
    if (!isLoading && target !== phoneNumber) {
      const err: NOSIDError = {
        ErrorCode: ErrorCodes.InvalidPhone,
        Code: ErrorCodes.InvalidPhone,
      };
      throw err;
    }
  }, [isLoading, target, phoneNumber]);
  const resendCode = () => {
    if (isSending || !phoneNumber) return;
    if (captcha.challenge && !captcha.token) return;
    sendCode({ targetId: phoneNumber, type: 'Sms', captcha, resend: true });
  };
  const form = useForm(
    {
      code: {
        value: '',
        id: 'code',
        validator: z
          .string()
          .length(
            widgets.ValidationCodeLength,
            'Common.ContactValidation.CodeInput.Errors.CodeRequired',
          ),
      },
    },
    {
      focusOnError: true,
      onError: logger.logFormErrors.bind(logger),
    },
  );

  const handleSubmit = async ({ code }: { code: string }) => {
    try {
      await enable2Fa({ code, target });
      navigate({ to: '/security' });
    } catch (e) {
      const nosidError = (e as AxiosError).response?.data as NOSIDError;
      const isInvalidCodeError = nosidError.ErrorCode === ErrorCodes.InvalidSecurityCode;
      if (!isInvalidCodeError) return;
      form.addError('code', 'Common.ContactValidation.CodeInput.Errors.InvalidCode');
    }
  };

  const props: TwoFactorCodeProps = {
    codeValue: form.value.code,
    codeId: form.ids.code ?? 'code',
    codeErrors: form.errors.code,
    isValidating: form.loading || isLoading,
    isSending,
    captchaChallenge: captcha.challenge,
    onCaptchaChange: captcha.setToken,
    onCodeChange: (v) => form.setValue('code', v),
    onResend: resendCode,
    onSubmit: form.handleSubmit(handleSubmit),
  };
  return templates.getSkinSelector(getSkinConfig(props));
}

function getSkinConfig(props: TwoFactorCodeProps): SkinPageConfig {
  return {
    skins: [],
    defaultValue: <TwoFactorCodeLight {...props} />,
  };
}
